import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../services/api';
import { Users, Plus, Edit2, Trash2, Key, X } from 'lucide-react';

export default function UsersAdmin() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [passwordUserId, setPasswordUserId] = useState(null);
    const [form, setForm] = useState({ fullName: '', username: '', email: '', password: '', role: 'admin' });
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const fetchUsers = async () => {
        try {
            const res = await userAPI.getAll();
            setUsers(res.data);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    useEffect(() => { fetchUsers(); }, []);

    const showMsg = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 4000);
    };

    const openCreate = () => {
        setEditingUser(null);
        setForm({ fullName: '', username: '', email: '', password: '', role: 'admin' });
        setShowModal(true);
    };

    const openEdit = (u) => {
        setEditingUser(u);
        setForm({ fullName: u.fullName || '', username: u.username, email: u.email, password: '', role: u.role });
        setShowModal(true);
    };

    const openPasswordChange = (userId) => {
        setPasswordUserId(userId);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.username || !form.email) {
            showMsg('error', 'Kullanıcı adı ve email zorunlu');
            return;
        }
        if (!editingUser && !form.password) {
            showMsg('error', 'Şifre zorunlu');
            return;
        }

        setSaving(true);
        try {
            if (editingUser) {
                await userAPI.update(editingUser.id, {
                    fullName: form.fullName,
                    username: form.username,
                    email: form.email,
                    role: form.role
                });
                showMsg('success', 'Kullanıcı güncellendi');
            } else {
                await userAPI.create(form);
                showMsg('success', 'Kullanıcı oluşturuldu');
            }
            setShowModal(false);
            fetchUsers();
        } catch (err) {
            showMsg('error', err.response?.data?.error || 'Hata oluştu');
        }
        setSaving(false);
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (!passwordForm.newPassword) {
            showMsg('error', 'Yeni şifre zorunlu');
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            showMsg('error', 'Şifreler eşleşmiyor');
            return;
        }

        setSaving(true);
        try {
            const payload = { newPassword: passwordForm.newPassword };
            if (passwordUserId === currentUser?.id) {
                payload.currentPassword = passwordForm.currentPassword;
            }
            await userAPI.changePassword(passwordUserId, payload);
            showMsg('success', 'Şifre değiştirildi');
            setShowPasswordModal(false);
        } catch (err) {
            showMsg('error', err.response?.data?.error || 'Hata oluştu');
        }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        try {
            await userAPI.delete(id);
            showMsg('success', 'Kullanıcı silindi');
            setDeleteConfirm(null);
            fetchUsers();
        } catch (err) {
            showMsg('error', err.response?.data?.error || 'Hata oluştu');
            setDeleteConfirm(null);
        }
    };

    const roleBadge = (role) => {
        const colors = {
            admin: { bg: 'rgba(108,92,231,0.1)', color: '#6c5ce7' },
            editor: { bg: 'rgba(0,184,148,0.1)', color: '#00b894' },
            viewer: { bg: 'rgba(253,203,110,0.15)', color: '#e17055' }
        };
        const c = colors[role] || colors.admin;
        return (
            <span className="category-badge" style={{ background: c.bg, color: c.color }}>
                {role}
            </span>
        );
    };

    const actionButtons = (u) => (
        <div style={{ display: 'flex', gap: '6px' }}>
            <button className="btn-icon" title="Düzenle" onClick={() => openEdit(u)}>
                <Edit2 size={15} />
            </button>
            <button className="btn-icon" title="Şifre Değiştir" onClick={() => openPasswordChange(u.id)}>
                <Key size={15} />
            </button>
            {u.id !== currentUser?.id && (
                <button className="btn-icon" title="Sil" onClick={() => setDeleteConfirm(u)} style={{ color: 'var(--danger)' }}>
                    <Trash2 size={15} />
                </button>
            )}
        </div>
    );

    if (loading) return <div className="loading"><div className="spinner"></div></div>;

    return (
        <div>
            <div className="admin-header">
                <div>
                    <h1>Kullanıcılar</h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: '2px' }}>Kullanıcı yönetimi</p>
                </div>
                <button className="btn btn-primary" onClick={openCreate}>
                    <Plus size={16} /> Yeni Kullanıcı
                </button>
            </div>

            {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

            {/* Desktop Table */}
            <div className="data-table-wrapper users-desktop-table">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Ad Soyad</th>
                            <th>Kullanıcı Adı</th>
                            <th>Email</th>
                            <th>Rol</th>
                            <th>Kayıt Tarihi</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id}>
                                <td style={{ fontWeight: 600 }}>{u.fullName || '-'}</td>
                                <td>{u.username}</td>
                                <td>{u.email}</td>
                                <td>{roleBadge(u.role)}</td>
                                <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString('tr-TR') : '-'}
                                </td>
                                <td>{actionButtons(u)}</td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Kullanıcı bulunamadı</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile Cards */}
            <div className="users-mobile-cards">
                {users.map(u => (
                    <div key={u.id} className="user-mobile-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '2px' }}>
                                    {u.fullName || u.username}
                                </div>
                                {u.fullName && (
                                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>@{u.username}</div>
                                )}
                            </div>
                            {roleBadge(u.role)}
                        </div>
                        <div style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginBottom: '4px', wordBreak: 'break-all' }}>
                            {u.email}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
                            Kayıt: {u.createdAt ? new Date(u.createdAt).toLocaleDateString('tr-TR') : '-'}
                        </div>
                        {actionButtons(u)}
                    </div>
                ))}
                {users.length === 0 && (
                    <div className="empty-state" style={{ padding: '2rem' }}>
                        <p>Kullanıcı bulunamadı</p>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h3>{editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</h3>
                            <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleSubmit} style={{ padding: '1.25rem 1.5rem' }}>
                            <div className="form-group">
                                <label>Ad Soyad</label>
                                <input type="text" className="form-control" value={form.fullName}
                                    onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} placeholder="Örn: Ahmet Yılmaz" />
                            </div>
                            <div className="form-group">
                                <label>Kullanıcı Adı *</label>
                                <input type="text" className="form-control" value={form.username}
                                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required />
                            </div>
                            <div className="form-group">
                                <label>Email *</label>
                                <input type="email" className="form-control" value={form.email}
                                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
                            </div>
                            {!editingUser && (
                                <div className="form-group">
                                    <label>Şifre *</label>
                                    <input type="password" className="form-control" value={form.password}
                                        onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={4} />
                                </div>
                            )}
                            <div className="form-group">
                                <label>Rol</label>
                                <select className="form-control" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                                    <option value="admin">Admin</option>
                                    <option value="editor">Editor</option>
                                    <option value="viewer">Viewer</option>
                                </select>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? 'Kaydediliyor...' : editingUser ? 'Güncelle' : 'Oluştur'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Password Change Modal */}
            {showPasswordModal && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
                        <div className="modal-header">
                            <h3>Şifre Değiştir</h3>
                            <button className="btn-icon" onClick={() => setShowPasswordModal(false)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handlePasswordChange} style={{ padding: '1.25rem 1.5rem' }}>
                            {passwordUserId === currentUser?.id && (
                                <div className="form-group">
                                    <label>Mevcut Şifre *</label>
                                    <input type="password" className="form-control" value={passwordForm.currentPassword}
                                        onChange={e => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))} required />
                                </div>
                            )}
                            <div className="form-group">
                                <label>Yeni Şifre *</label>
                                <input type="password" className="form-control" value={passwordForm.newPassword}
                                    onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))} required minLength={4} />
                            </div>
                            <div className="form-group">
                                <label>Yeni Şifre (Tekrar) *</label>
                                <input type="password" className="form-control" value={passwordForm.confirmPassword}
                                    onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))} required minLength={4} />
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>İptal</button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {deleteConfirm && (
                <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h3>Kullanıcı Sil</h3>
                            <button className="btn-icon" onClick={() => setDeleteConfirm(null)}><X size={18} /></button>
                        </div>
                        <div style={{ padding: '1.25rem 1.5rem' }}>
                            <p style={{ margin: '0 0 1.25rem 0' }}>
                                <strong>{deleteConfirm.fullName || deleteConfirm.username}</strong> adlı kullanıcıyı silmek istediğinize emin misiniz?
                            </p>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>İptal</button>
                                <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Sil</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
